import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/supabase-auth';

export interface MfaRequirement {
  id: string;
  user_id: string;
  role: 'admin' | 'organization_admin' | 'parent';
  mfa_required: boolean;
  enforced_at: string | null;
  grace_period_ends: string | null;
  created_at: string;
  updated_at: string;
}

export function useMfaRequirement() {
  const { session } = useAuth();

  const { data: requirement, isLoading } = useQuery({
    queryKey: ['mfa-requirement', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return null;

      const { data, error } = await supabase
        .from('mfa_requirements')
        .select('*')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (error) throw error;
      return data as MfaRequirement | null;
    },
    enabled: !!session?.user?.id,
  });

  // Check if user actually has MFA enabled via auth.mfa_factors
  const { data: mfaFactors, isLoading: isLoadingFactors } = useQuery({
    queryKey: ['mfa-factors', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return [];
      const { data } = await supabase.auth.mfa.listFactors();
      return data?.all || [];
    },
    enabled: !!session?.user?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const hasMfaEnabled = mfaFactors?.some((factor) => factor.status === 'verified') || false;
  const requiresMfa = requirement?.mfa_required && !hasMfaEnabled;
  
  // FIX: Only enter grace period if user does NOT have MFA enabled
  // If user already has verified MFA factors, they should not see grace period warnings
  const isInGracePeriod = requirement?.grace_period_ends && !hasMfaEnabled
    ? new Date(requirement.grace_period_ends) > new Date()
    : false;
  
  const isEnforcementRequired = requiresMfa && !isInGracePeriod;

  return {
    requirement,
    requiresMfa,
    hasMfaEnabled,
    isInGracePeriod,
    isEnforcementRequired,
    gracePeriodDays: requirement?.grace_period_ends
      ? Math.ceil(
          (new Date(requirement.grace_period_ends).getTime() - Date.now()) /
            (1000 * 60 * 60 * 24)
        )
      : 0,
    isLoading: isLoading || isLoadingFactors, // Wait for BOTH queries to complete
  };
}
